import { FC, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  UpdateUserLastActionDate,
  HandleLogOut,
} from "../../helper/ui/methods";
import { useAppStateContext } from "../../middleware/context/state-context-provider";
import { ProjectInterface } from "../../middleware/interfaces/project/project-interface";
import { UserSubscriptionRetrieveInterface } from "../../middleware/interfaces/user/user-subscription-retrieve-interface";
import { UserSubscriptionInterface } from "../../middleware/interfaces/user/user-subscription-interface";
import ResponseDialog from "../ui/user/response-dialog";
import UserBinaryDecisionDialog from "../ui/user/user-binary-decision-dialog";
import UserSubscriptionDialog from "../ui/user/user-subscription-dialog";
import UserProfileDialog from "../ui/user/user-profile-dialog";
import UserExternalConnectionDialog from "../ui/user/user-external-connection-dialog";
import MenuButton from "../ui/user/menu-button";
import ProjectInfo from "../ui/project/project-info";
import ProjectDialog from "../ui/project/project-dialog";
import ProjectIcon from "../ui/project/project-icon";
import ProjectTeamDialog from "../ui/project/project-team-dialog";
import ProjectShareDialog from "../ui/project/project-share-dialog";
import MainNavigationPanel from "../ui/user/main-navigation-panel";
import MainFooterPanel from "../ui/user/main-footer-panel";
import CanvasHeader from "../ui/user/canvas-header";
import CanvasEmptyState from "../ui/user/canvas-empty-state";
import "../../styles/dashboard.css";

export const Dashboard: FC = () => {
  const [state, dispatch] = useAppStateContext();
  const [selectedProject, setSelectedProject] =
    useState<ProjectInterface | null>(null);
  const [projectDialogReveal, setProjectDialogReveal] = useState("");
  const [projectShareDialogReveal, setProjectShareDialogReveal] =
    useState(false);
  const [userProfileDialogReveal, setUserProfileDialogReveal] = useState(false);
  const [
    userExternalConnectionDialogReveal,
    setUserExternalConnectionDialogReveal,
  ] = useState(false);
  const [userSubscriptionDialogReveal, setUserSubscriptionDialogReveal] =
    useState(false);
  const [userSubscriptionData, setUserSubscriptionData] =
    useState<UserSubscriptionInterface | null>(null);

  useEffect(() => {
    if (state.user) {
      UpdateUserLastActionDate(state.user, dispatch);
    }
  }, [state.user]);

  useEffect(() => {
    if (state.decision) {
      if (state.decision.result !== "") {
        switch (state.decision.result) {
          case "DELETE_PROJECT":
          case "EDIT_PROJECT":
            dispatch({
              type: state.decision.result,
              payload: state.decision.data,
            });
            setSelectedProject(null);
            break;
          case "DOWNLOAD_PROJECT":
          case "EDIT_PROFILE":
          case "EDIT_PROJECT_TEAM":
            dispatch({
              type: state.decision.result,
              payload: state.decision.data,
            });
            break;
          case "NO_ACTION":
            console.log("No action taken!");
            break;
          default:
            // Optionally handle any cases not covered above
            console.log(`Unknown decision result: ${state.decision.result}`);
        }
        // This runs after any decision was made!
        dispatch({ type: "DECISION", decision: null });
      }
    }
  }, [state.decision?.result]);

  useEffect(() => {
    if (state.openUrl) {
      window.open(state.openUrl, "_blank");
      dispatch({ type: "OPEN_URL", payload: null });
    }
  }, [state.openUrl]);

  useEffect(() => {
    if (userSubscriptionDialogReveal === true) {
      const userSubscriptionRetrieveData: UserSubscriptionRetrieveInterface = {
        email: state.user!.email,
        type: "UserSubscriptionRetrieveInterface",
      };
      dispatch({
        type: "RETRIEVE_USER_SUBSCRIPTION",
        payload: userSubscriptionRetrieveData,
      });
      setUserSubscriptionDialogReveal(false);
    }
  }, [userSubscriptionDialogReveal]);

  return (
    <>
      {(() => {
        if (state.user) {
          return (
            <div
              id="pageWrapper"
              onClick={() => UpdateUserLastActionDate(state.user!, dispatch)}
            >
              <MainNavigationPanel user={state.user} />
              <div id="contentWrapper">
                <div id="leftMenu">
                  <div className="menuButtonWrapper">
                    <MenuButton
                      imageSource="assets/icons/add-project.png"
                      alternativeText="CREATE PROJECT"
                      dispatch={() => {
                        setProjectDialogReveal("CREATE");
                      }}
                    />
                    <MenuButton
                      imageSource="assets/icons/profile.png"
                      alternativeText="EDIT PROFILE"
                      dispatch={() => {
                        setUserProfileDialogReveal(true);
                      }}
                    />
                    <MenuButton
                      imageSource="assets/icons/external-connection.png"
                      alternativeText="EXTERNAL CONNECTION"
                      dispatch={() => {
                        setUserExternalConnectionDialogReveal(true);
                      }}
                    />
                    <MenuButton
                      imageSource="assets/icons/subscription.png"
                      alternativeText="SUBSCRIPTION"
                      dispatch={() => {
                        setUserSubscriptionDialogReveal(true);
                      }}
                    />
                    <MenuButton
                      imageSource="assets/icons/logout.png"
                      alternativeText="LOG OUT"
                      dispatch={() => {
                        HandleLogOut(dispatch);
                      }}
                    />
                  </div>
                </div>
                <div id="content" className="canvasSurface">
                  <CanvasHeader
                    crumbs={["PROJECTS"]}
                    itemCount={state.user.projects?.length ?? 0}
                    itemNoun="project"
                    selectedCount={selectedProject ? 1 : 0}
                  />
                  <div
                    id="projectCanvas"
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        setSelectedProject(null);
                      }
                    }}
                  >
                    {state.user.projects && state.user.projects.length > 0 ? (
                      state.user.projects.map((project) => (
                        <ProjectIcon
                          key={project.projectID}
                          projectName={project.projectName}
                          projectLocation={project.projectLocation!}
                          projectDescription={project.projectDescription!}
                          ProjectStartDate={project.projectStartDate!}
                          ProjectEndDate={project.projectEndDate!}
                          accessType={
                            project.email === state.user?.email
                              ? "OWNED"
                              : "SHARED"
                          }
                          isSelected={selectedProject === project}
                          onClick={() => {
                            setSelectedProject(project);
                          }}
                        />
                      ))
                    ) : (
                      <CanvasEmptyState
                        imageSource="assets/icons/project.png"
                        title="No projects yet"
                        message="Create a project to start organising models, files, and clash reports."
                        primaryActionLabel="New project"
                        primaryAction={() => {
                          setProjectDialogReveal("CREATE");
                        }}
                      />
                    )}
                  </div>
                  <ProjectDialog
                    reveal={projectDialogReveal}
                    user={state.user}
                    setProjectDialogReveal={setProjectDialogReveal}
                    selectedProject={selectedProject}
                    dispatch={dispatch}
                  />
                  <UserProfileDialog
                    reveal={userProfileDialogReveal}
                    user={state.user}
                    setProfileDialogReveal={setUserProfileDialogReveal}
                    dispatch={dispatch}
                  />
                  <UserExternalConnectionDialog
                    reveal={userExternalConnectionDialogReveal}
                    user={state.user}
                    setUserExternalConnectionDialogReveal={
                      setUserExternalConnectionDialogReveal
                    }
                    dispatch={dispatch}
                  />
                  <UserSubscriptionDialog
                    user={state.user}
                    dispatch={dispatch}
                  />
                  <ProjectShareDialog
                    user={state.user}
                    selectedProject={selectedProject!}
                    reveal={projectShareDialogReveal}
                    setProjectShareDialogReveal={setProjectShareDialogReveal}
                    dispatch={dispatch}
                  />
                  <ProjectTeamDialog
                    user={state.user}
                    projectTeam={state.projectTeam}
                    dispatch={dispatch}
                  />
                  <UserBinaryDecisionDialog
                    decision={state.decision}
                    dispatch={dispatch}
                  />
                  <ResponseDialog
                    title="Server Response"
                    message={state.response}
                    progress={state.uploadProgress}
                    archiveProgress={state.archiveProgress}
                    dispatch={dispatch}
                  />
                  <ProjectTeamDialog
                    user={state.user}
                    projectTeam={state.projectTeam}
                    dispatch={dispatch}
                  />
                </div>
                <div id="rightMenu">
                  {selectedProject !== null ? (
                    <ProjectInfo
                      state={state}
                      selectedProject={selectedProject}
                      setSelectedProject={setSelectedProject}
                      setProjectShareDialogReveal={setProjectShareDialogReveal}
                      setProjectEditDialogReveal={setProjectDialogReveal}
                      dispatch={dispatch}
                    />
                  ) : null}
                </div>
              </div>
              <MainFooterPanel />
            </div>
          );
        } else {
          return <Navigate to={"/authentication&registration"} replace />;
        }
      })()}
    </>
  );
};
